import { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { recordActions, Auth } from '../../config/configuration/react';
import { NRM_FIRM_STATUS, NRM_SUB_STATUS } from '../../config';
import { translateDate } from '../../components/common/Translate';

export const useGuaranteedPrice = (recordId) => {
  const dispatch = useDispatch();
  const preEmptRef = useRef(null);
  const [isGuaranteeLoading, setIsGuaranteeLoading] = useState(false);
  const [firstTooltipOpen, setFirstTooltipOpen] = useState(false);
  const [secondTooltipOpen, setSecondTooltipOpen] = useState(false);
  const [thirdTooltipOpen, setThirdTooltipOpen] = useState(false);
  const [firstCollapse, setFirstCollapse] = useState(false);
  const [secondCollapse, setSecondCollapse] = useState(false);
  const [thirdCollapse, setThirdCollapse] = useState(false);
  const [fourthTooltipOpen, setFourthTooltipOpen] = useState(false);
  const [fourthCollapse, setFourthCollapse] = useState(false);
  const [fifthTooltipOpen, setFifthTooltipOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [fifthCollapse, setFifthCollapse] = useState(false);
  const [isOpenPreEmption, setIsOpenPreEmption] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [orderConfirmationModalOpen, setOrderConfirmationModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [offerId, setOfferId] = useState('');
  const [newCarBrand, setNewCarBrand] = useState('');
  const [newCarModel, setNewCarModel] = useState('');
  const guaranteedPrice = useSelector((state) => state.reduxRecords.data[recordId]?.guaranteedPrice || []);
  const record = useSelector((state) => state.reduxRecords.data[recordId]?.record || []);
  const counterInspection = useSelector((state) => state.reduxRecords.data[recordId]?.counterInspection || []);
  const firmValuation = useSelector((state) => state.reduxRecords.data[recordId]?.firmValuation || []);
  const supervisor = useSelector((state) => state.reduxRecords.data[recordId]?.supervisor || []);
  const nrmStatusId = useSelector((state) => state.reduxRecords.data[recordId])?.record?.nrmStatusId;
  const originId = useSelector((state) => state?.reduxRecords?.data[recordId]?.record?.originId);
  const nrmStatusLabel = useSelector((state) => state.reduxRecords.data[recordId])?.record?.nrmStatusLabel;
  const cancelCaseId = useSelector((state) => state.reduxRecords.data[recordId])?.record?.cancelCaseId;
  const supervisorEntryStockDate = useSelector(
    (state) => state.reduxRecords.data[recordId]?.supervisor?.entryStockDate?.date,
  );
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState(translateDate(moment(supervisorEntryStockDate)));
  const history = useHistory();
  const isNrmUser = Auth.isNrmUser();
  const isSmartContract = Auth.isSmartContract();
  const isSolContract = Auth.isSolContract();
  const isEnabled =
    offerId.length > 0 && newCarBrand.length > 0 && newCarModel.length > 0 && expectedDeliveryDate.length > 0;
  const roleIsViewer = Auth.roleViewerAccess();
  const {
    pricingInProgress,
    noTradeInPriceProposed,
    tradeInExpired,
    tradeInCancelled,
    tradeInPriceProposed,
    tradeInLinkedToNcNotConfirmedByClient,
    tradeInConfirmedByClient,
    tradeInWithOrderId,
    counterAppraisalVerification,
    tradeInMaintainedPriceMaintained,
    tradeInMaintainedPriceModified,
  } = NRM_SUB_STATUS;
  const { validated, confirmed } = NRM_FIRM_STATUS;
  const isAllowedToLinkNcOrders = [
    tradeInPriceProposed,
    tradeInLinkedToNcNotConfirmedByClient,
    tradeInConfirmedByClient,
  ].includes(nrmStatusId);
  const toggleFirst = () => {
    setFirstCollapse(!firstCollapse);
  };

  const toggleSecond = () => {
    setSecondCollapse(!secondCollapse);
  };
  const toggleThird = () => {
    setThirdCollapse(!thirdCollapse);
  };
  const toggleFifth = () => {
    setFifthCollapse(!fifthCollapse);
  };

  const displayNrmInfos = () => {
    return ![pricingInProgress, noTradeInPriceProposed, tradeInExpired, tradeInCancelled].includes(nrmStatusId);
  };

  const nrmStatusColor = (statusId) => {
    let color;
    switch (statusId) {
      case pricingInProgress:
        color = 'status-brown';
        break;
      case tradeInPriceProposed:
      case tradeInLinkedToNcNotConfirmedByClient:
      case tradeInConfirmedByClient:
      case tradeInWithOrderId:
        color = 'status-green';
        break;
      case counterAppraisalVerification:
      case tradeInMaintainedPriceMaintained:
      case tradeInMaintainedPriceModified:
        color = 'status-gray';
        break;
      case 35:
        color = 'status-red';
        break;
      default:
        color = 'status-red';
        break;
    }
    return color;
  };
  const fourthToggle = () => {
    setFourthCollapse(!fourthCollapse);
  };

  const scrollToPreEmption = () => {
    setFourthCollapse(true);
    setTimeout(() => {
      return preEmptRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 500);
  };

  const ncOrder = () => {
    if (firmValuation.length !== 0) {
      const fcoId = firmValuation.filter((item) => item.fcoId !== null);
      if (fcoId.length !== 0) return fcoId[0].fcoId;
    }
    return null;
  };

  const getValuationDataByStatus = () => {
    let price = guaranteedPrice?.guaranteeablePrice;
    let date = guaranteedPrice.baseDate;
    let customerRealDeliveryDate = null;
    let dealershipCompoundExpectedDate = null;
    let dealershipCompoundRealDate = null;
    let customerAppointmentDeliveryDate = null;
    if (firmValuation && firmValuation.length !== 0) {
      const defaultValuation = firmValuation[firmValuation.length - 1];
      const validatedValuation = firmValuation.filter((item) => item.firmStatusId === validated);
      const confirmedValuation = firmValuation.filter((item) => item.firmStatusId === confirmed);
      price = defaultValuation.firmTTCPrice;
      date = defaultValuation.expectedDeliveryDate;
      if (confirmedValuation.length > 0) {
        price = confirmedValuation[0].firmTTCPrice;
        date = confirmedValuation[0].expectedDeliveryDate;
        customerRealDeliveryDate = confirmedValuation[0].customerRealDeliveryDate;
      } else if (validatedValuation.length > 0) {
        price = validatedValuation[0].firmTTCPrice;
        date = validatedValuation[0].expectedDeliveryDate;
        dealershipCompoundExpectedDate = validatedValuation[0].dealershipCompoundExpectedDate;
        dealershipCompoundRealDate = validatedValuation[0].dealershipCompoundRealDate;
        customerAppointmentDeliveryDate = validatedValuation[0].customerAppointmentDeliveryDate;
      }
    }
    return {
      price,
      date,
      customerRealDeliveryDate,
      dealershipCompoundExpectedDate,
      dealershipCompoundRealDate,
      customerAppointmentDeliveryDate,
    };
  };

  const requestGuaranteedPrice = async (id) => {
    try {
      setIsGuaranteeLoading(true);
      const res = await dispatch(recordActions.requestGuaranteedPrice(id));
      if (res?.data?.id) {
        setIsGuaranteeLoading(false);
        history.push(`/files/${id}`);
      }
      setIsGuaranteeLoading(false);
    } catch (error) {
      setIsGuaranteeLoading(false);
      console.log(error);
    }
  };

  const toggleHistory = () => {
    setHistoryOpen(!historyOpen);
  };

  const handleKeepCar = (nrmRecordId, keepCar, choiceMade) => {
    dispatch(recordActions.putSupervisorInfos(recordId, { nrmRecordId, keepCar, originId }));
    if (choiceMade === true) {
      history.push(`/files/${recordId}`);
    }
  };

  const handleUpdateButtonClick = (rowData) => {
    setSelectedRowData(rowData);
    setSelectedDate(rowData.expectedDeliveryDate ? new Date(rowData.expectedDeliveryDate.date) : null);
    setUpdateModalOpen(true);
  };

  const handleOrderConfirmationButtonClick = (rowData) => {
    setSelectedRowData(rowData);
    setSelectedDate(rowData.expectedDeliveryDate ? new Date(rowData.expectedDeliveryDate.date) : null);
    setOrderConfirmationModalOpen(true);
  };
  const handleDateUpdateConfirm = async () => {
    try {
      setSelectedRowData({
        ...selectedRowData,
        expectedDeliveryDate: moment(selectedDate).format('YYYY-MM-DD'),
      });
      await dispatch(
        recordActions.putFirmValuation(selectedRowData.id, {
          expectedDeliveryDate: moment(selectedDate).format('YYYY-MM-DD'),
        }),
      );
      setUpdateModalOpen(false);
      history.push(`/files/${recordId}`);
    } catch (e) {
      console.log(e);
    }
  };

  const handleOrderConfirm = async () => {
    try {
      const updatedRowData = {
        ...selectedRowData,
        expectedDeliveryDate: moment(selectedDate).format('YYYY-MM-DD'),
        firmStatusId: 15,
      };
      await dispatch(
        recordActions.putFirmValuation(selectedRowData.id, {
          expectedDeliveryDate: moment(selectedDate).format('YYYY-MM-DD'),
          firmStatusId: 15,
        }),
      );
      setSelectedRowData(updatedRowData);
      setOrderConfirmationModalOpen(false);
      history.push(`/files/${recordId}`);
    } catch (e) {
      console.log(e);
    }
  };

  /** *********** NC order **************** */
  const handleConfirmNCOrder = () => {
    try {
      dispatch(
        recordActions.putNCOrderLink(guaranteedPrice?.nrmRecordId, {
          offerId,
          newCarBrand,
          newCarModel,
          expectedDeliveryDate,
        }),
      );
      setHistoryOpen(false);
      history.push(`/files/${recordId}`);
    } catch (error) {
      console.log(error);
    }
  };

  const handleSelectedDate = (date) => {
    if (date !== null) {
      setExpectedDeliveryDate(moment(date).format('YYYY/MM/DD'));
    }
  };

  /** *********** NC order **************** */

  return {
    firstTooltipOpen,
    secondTooltipOpen,
    thirdTooltipOpen,
    fourthTooltipOpen,
    setFirstTooltipOpen,
    setSecondTooltipOpen,
    setThirdTooltipOpen,
    setFourthTooltipOpen,
    setHistoryOpen,
    setOfferId,
    setNewCarBrand,
    setNewCarModel,
    setExpectedDeliveryDate: handleSelectedDate,
    firstCollapse,
    secondCollapse,
    thirdCollapse,
    fourthCollapse,
    offerId,
    newCarBrand,
    newCarModel,
    toggleSecond,
    toggleFirst,
    toggleThird,
    fourthToggle,
    toggleHistory,
    historyOpen,
    guaranteedPrice,
    requestGuaranteedPrice,
    isGuaranteeLoading,
    firmValuation,
    nrmStatusColor,
    ncOrder,
    getValuationDataByStatus,
    displayNrmInfos,
    handleKeepCar,
    supervisor,
    isNrmUser,
    isSolContract,
    isSmartContract,
    nrmStatusId,
    originId,
    nrmStatusLabel,
    record,
    fifthCollapse,
    toggleFifth,
    fifthTooltipOpen,
    setFifthTooltipOpen,
    isOpenPreEmption,
    setIsOpenPreEmption,
    scrollToPreEmption,
    preEmptRef,
    counterInspection,
    cancelCaseId,
    updateModalOpen,
    setUpdateModalOpen,
    orderConfirmationModalOpen,
    setOrderConfirmationModalOpen,
    selectedRowData,
    setSelectedRowData,
    selectedDate,
    setSelectedDate,
    handleUpdateButtonClick,
    handleOrderConfirmationButtonClick,
    handleConfirmNCOrder,
    isEnabled,
    expectedDeliveryDate: expectedDeliveryDate ? new Date(expectedDeliveryDate) : '',
    handleDateUpdateConfirm,
    handleOrderConfirm,
    isAllowedToLinkNcOrders,
    roleIsViewer,
  };
};
